<script>
import { mapGetters } from 'vuex'
import ExpenseRuleBlock from '@/components/organism/checkout/ExpenseRuleBlock.vue'
import MethodBlock from '@/components/organism/checkout/MethodBlock.vue'
import CardsBlock from '@/components/organism/checkout/CardsBlock.vue'
import ModalConfirm from '@/components/organism/modal/ModalConfirm.vue'

export default {
    name: 'PaymentBlocks',
    components: { ModalConfirm, CardsBlock, MethodBlock, ExpenseRuleBlock },

    props: {
        stripe: {
            type: Object,
            default: () => {},
        },
    },

    computed: {
        ...mapGetters(['expenseRules', 'modalsOpen']),
        cart() {
            return this.$cart || {}
        },
        calculation() {
            return this.$cart?.calculation || {}
        },
        paymentMethods() {
            return (
                this.calculation.payment_methods?.reduce((p, c) => {
                    if (c !== this.$cardPayment) p.push(c.replace('-', '_'))
                    return p
                }, []) || []
            )
        },
        hasCardPayment() {
            return this.calculation.payment_methods?.includes(this.$cardPayment)
        },
        usableExpenseRules() {
            return (
                this.expenseRules?.filter(
                    (rule) =>
                        !!this.calculation.usable_expense_rules?.find(
                            (usableRules) => usableRules.id === rule.id
                        )
                ) || []
            )
        },
        disabledExpenseRules() {
            return (
                this.expenseRules?.filter(
                    (rule) =>
                        !this.calculation.usable_expense_rules?.find(
                            (usableRules) => usableRules.id === rule.id
                        )
                ) || []
            )
        },
        selectedPaymentMethod() {
            return this.cart.payment_method && !this.cart.selected_expense_rule
                ? this.cart.payment_method.replace('-', '_')
                : null
        },
    },

    methods: {
        changeMethod(payload) {
            if (
                payload.value &&
                (payload.isRule
                    ? payload.value.id !== this.getId(this.cart.selected_expense_rule)
                    : payload.value !== this.cart.payment_method || this.cart.selected_expense_rule)
            ) {
                if (
                    this.calculation.group_order_employees?.length ||
                    this.calculation.expense_code?.length
                ) {
                    this.$refs.changeMethod.open(payload)
                } else {
                    if (payload.isRule) this.setExpenseRule(payload.value)
                    else this.setPaymentMethod(payload.value)
                }
            }
        },
        async confirmMethodChange(payload) {
            this.cart.expense_code = undefined
            if (payload.isRule) await this.setExpenseRule(payload.value)
            else await this.setPaymentMethod(payload.value)
            return Promise.resolve()
        },
        async setExpenseRule(rule) {
            await this.cart.resetGroupOrder()
            await this.cart.resetAllowance()
            this.cart.payment_method = null
            this.cart.selected_expense_rule = rule
            await this.$store.dispatch('setAllowance', rule)
            await this.cart.calculate()
            return Promise.resolve()
        },
        async setPaymentMethod(method) {
            await this.cart.resetGroupOrder()
            await this.cart.resetAllowance()
            this.cart.payment_method = method.replace('_', '-')
            await this.cart.calculate()
            return Promise.resolve()
        },
    },
}
</script>

<template lang="pug">
.payments-block
    expense-rule-block(
        v-for="(rule, index) in usableExpenseRules"
        :value="cart.selected_expense_rule"
        @input="changeMethod({isRule: true, value: $event})"
        :model="rule"
        :stripe="stripe"
        :id="`expenseRule-${rule.id}`"
        :key="`expenseRule-${index}`")
    expense-rule-block(
        v-if="!cart.cloudcanteen_id"
        :class="{'border-0': index + 1 === disabledExpenseRules.length && !paymentMethods.length && !hasCardPayment, 'border-top-1': !index}"
        v-for="(rule, index) in disabledExpenseRules"
        :model="rule"
        :key="`disabledExpenseRule-${index}`"
        disabled)
    method-block(
        v-if="!cart.isCanteenGroupOrder"
        v-for="(method, index) in paymentMethods"
        :class="{'border-0': index + 1 === paymentMethods.length && !hasCardPayment, 'border-top-1': !index}"
        :value="selectedPaymentMethod"
        @input="changeMethod({isRule: false, value: $event})"
        :method="method"
        :key="`method-${index}`")
    cards-block(
        v-if="hasCardPayment"
        :value="selectedPaymentMethod"
        @input="changeMethod({isRule: false, value: $event})"
        :stripe="stripe"
        id="select_card")

    modal-confirm(
        ref="changeMethod"
        header-level="h1"
        :header-label="$t('order.payment_method_change.title')"
        :body-label="$t('order.payment_method_change.desc')"
        :cancel-label="$t('order.payment_method_change.stick')"
        :confirm-label="$t('order.payment_method_change.discard')"
        :action="confirmMethodChange"
        hide-button)
</template>
<style lang="scss">
.payments-block > .cards-block,
.payments-block > .cards-block > .cards-wrapper,
.payments-block {
    > .radio-line-wrapper {
        &:not(:first-child):not(:last-child) {
            border-radius: 0;
        }
        &:not(:last-child) {
            border-bottom: 0;
        }
        &:first-child {
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
        }
        &:last-child {
            border-top-right-radius: 0;
            border-top-left-radius: 0;
        }
    }
}
.payments-block > .cards-block > .cards-wrapper:not(:first-child) {
    > .radio-line-wrapper {
        &:first-child {
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
        }
    }
}
.payments-block .radio-line-wrapper.active {
    outline: 2px solid var(--black);
    z-index: 1;
    border-radius: var(--border-radius-xs) !important;
    background-color: var(--dark-f7);
}
.payments-block .radio-line-wrapper .radio-line.reverse {
    justify-content: flex-start !important;
    gap: 8px;
}
</style>
